


import { SimpleSelect, Flex, View, IconRefreshSolid, IconButton } from '@smarterservices/smarter-ui';
import { useEffect, useState } from 'react';
import { useAppStore } from '../../../stores/app';
import Loader from '../../Common/Loader';

/**
 * RemoteCameraList Component
 * 
 * Displays a dropdown list of available cameras from a remote second camera participant
 * and allows the user to switch between them. The component handles real-time updates
 * when camera changes occur and provides loading states during operations.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.mediaAgent - LiveKit media agent for RPC communication
 * @param {Function} [props.onCameraChangeStart] - Callback fired when camera change starts
 * @param {Function} [props.onCameraChangeEnd] - Callback fired when camera change completes
 * @param {boolean} props.onCameraChangeEnd.successful - Whether the change was successful
 * @param {Object} [props.onCameraChangeEnd.data] - Response data on success
 * @param {Error} [props.onCameraChangeEnd.error] - Error object on failure
 * @returns {React.ReactElement} Camera selection interface with dropdown and refresh button
 */
const RemoteCameraList = ({ 
  mediaAgent, 
  onCameraChangeStart = () => {}, 
  onCameraChangeEnd = () => {} 
}) => {

  // State for managing camera list and selection
  const [cameraList, setCameraList] = useState([]);
  const [isLoadingVideoDevices, setIsLoadingVideoDevices] = useState(true);
  const [selectedDeviceLabel, setSelectedDeviceLabel] = useState('');
  const [selectedDeviceId, setSelectedDeviceId] = useState('');

  /**
   * Effect to listen for real-time camera device changes from the remote participant
   * Updates local state when the remote participant changes their camera
   */
  useEffect(() => {
    if (!mediaAgent) {
      return;
    }
    
    // Listen for data messages from the remote participant
    mediaAgent.getRoom().on('dataReceived', (payload, participant, kind) => {
      const dataMessage = mediaAgent.processDataMessage(payload, participant, kind);
      
      // Update selection when remote participant changes camera
      if (dataMessage.event === 'secondCameraDeviceChange') {
        setSelectedDeviceId(dataMessage.data.payload.deviceId);
        setSelectedDeviceLabel(dataMessage.data.payload.deviceLabel);
      }
    });
  }, [mediaAgent]);


  /**
   * Fetches the list of available cameras from the remote participant
   * Updates the camera list and current selection state
   */
  const getCameraList = async () => {
    try {      
      setIsLoadingVideoDevices(true);
      
      // Request camera list from remote participant via RPC
      const response = await mediaAgent.performRpc({
        destinationIdentity: useAppStore.getState().secondCameraIdentity,
        method: 'getCameraList'
      });

      const responseJson = JSON.parse(response);
      
      if (responseJson.successful) {
        setIsLoadingVideoDevices(false);
        setCameraList(responseJson.data.deviceList);
        setSelectedDeviceLabel(responseJson.data.currentDeviceLabel);
        setSelectedDeviceId(responseJson.data.currentDeviceId);
      }

    } catch (error) {
      console.error('RPC call failed:', error);
    }
  };

  /**
   * Effect to fetch camera list when mediaAgent becomes available
   */
  useEffect(() => {
    if (!mediaAgent) {
      return;
    }
    getCameraList();
  }, [mediaAgent]);

  /**
   * Handles camera selection change from the dropdown
   * Sends RPC request to remote participant to change their camera
   * 
   * @param {Event} event - The select change event
   * @param {Object} options - Select options containing id and value
   * @param {string} options.id - The device ID
   * @param {string} options.value - The device label
   */
  const handleSelectChange = async (event, { id, value }) => {
    try {
      // Notify parent component that camera change is starting
      onCameraChangeStart();
      
      // Send RPC request to change camera on remote participant
      const response = await mediaAgent.performRpc({
        destinationIdentity: useAppStore.getState().secondCameraIdentity,
        method: 'changeCamera',
        payload: JSON.stringify({ deviceLabel: value }),
      });

      try {
        const responseJson = JSON.parse(response);
        
        if (responseJson.successful) {
          // Update local state with new camera selection
          setSelectedDeviceLabel(responseJson.data.deviceLabel);
          setSelectedDeviceId(responseJson.data.deviceId);
          
          // Allow time for camera stream to initialize before notifying completion
          setTimeout(() => {
            onCameraChangeEnd({
              successful: true,
              data: responseJson.data,
            });
          }, 1000);
        }
      } catch (error) {
        onCameraChangeEnd({
          successful: false,
          error,
        });
        console.error('RPC call response not json:', error);
      }
    } catch (error) {
      onCameraChangeEnd({
        successful: false,
        error,
      });
      console.error('RPC call failed:', error);
    }
  };


  // Render camera selection interface
  return (
    <Flex
      direction="row"
      justifyItems="center"
      alignItems="center"
      margin="0 0 medium 0"
      width="100%"
    >
      <View as="div" width="400px">
        {/* Show loading state while fetching cameras */}
        {isLoadingVideoDevices && (
          <Loader 
            textSize="medium" 
            spinnerSize="x-small" 
            spinnerMargin="0 small 0 0" 
            textAs="span" 
            text="Loading webcams..." 
          />
        )}
        
        {/* Show camera selection dropdown when loaded */}
        {!isLoadingVideoDevices && (
          <SimpleSelect
            renderLabel=""
            assistiveText="Select the remote camera from the dropdown using the arrow keys"
            value={selectedDeviceLabel}
            size="medium"
            width="400px"
            onChange={handleSelectChange}
          >
            {cameraList.length > 0 ? (
              // Render available camera options
              cameraList.map((device) => (
                <SimpleSelect.Option
                  key={device.id}
                  id={device.id}
                  value={device.label}
                >
                  {device.label || `Camera ${cameraList.indexOf(device) + 1}`}
                </SimpleSelect.Option>
              ))
            ) : (
              // Show fallback when no cameras available
              <SimpleSelect.Option id="no-cams" value="no-cams">
                No webcams available
              </SimpleSelect.Option>
            )}
          </SimpleSelect>
        )}
      </View>
      
      {/* Refresh button to reload camera list */}
      <IconButton
        color="primary"
        margin="0 0 0 small"
        onClick={getCameraList}
        screenReaderLabel="Refresh"
      >
        <IconRefreshSolid />
      </IconButton>
    </Flex>
  );
};

export default RemoteCameraList;
